import { useState, useEffect } from 'react';
import { Settings, Save, Loader2 } from 'lucide-react';
import { ope } from '../../../services/api';
import { useToast } from '../../../context/ToastContext';

const OpeAdminRetoConfig = () => {
    const [config, setConfig] = useState({
        activo: false,
        inicio: '',
        reset_periodo: 'semanal',
        preguntas: 8,
        pts_participacion: 3,
        pts_acierto: 1
    });
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const { success, error } = useToast();

    useEffect(() => {
        loadConfig();
    }, []);

    const loadConfig = async () => {
        try {
            setLoading(true);
            const data = await ope.adminGetRetoConfig();
            setConfig({
                activo: data.activo || false,
                inicio: data.inicio || '',
                reset_periodo: data.reset_periodo || 'semanal',
                preguntas: data.preguntas || 8,
                pts_participacion: data.pts_participacion || 3,
                pts_acierto: data.pts_acierto || 1
            });
        } catch (err) {
            console.error('Error loading config:', err);
            error('Error al cargar la configuración del reto');
        } finally {
            setLoading(false);
        }
    };

    const handleSave = async () => {
        try {
            setSaving(true);
            await ope.adminUpdateRetoConfig(config);
            success('Configuración guardada correctamente');
        } catch (err) {
            console.error('Error saving config:', err);
            error('Error al guardar la configuración');
        } finally {
            setSaving(false);
        }
    };

    const handleChange = (field, value) => {
        setConfig(prev => ({ ...prev, [field]: value }));
    };

    if (loading) {
        return (
            <div className="flex items-center justify-center p-8">
                <Loader2 className="w-6 h-6 animate-spin text-indigo-400" />
            </div>
        );
    }

    return (
        <div className="p-4 bg-[var(--glass-bg)] border border-[var(--glass-border)] rounded-b-xl space-y-4">
            <div className="flex items-center justify-between mb-4">
                <h4 className="text-sm font-medium text-[var(--text-primary)] text-gray-400 uppercase tracking-wider">
                    Ajustes Generales del Reto Diario
                </h4>
                
                {/* Toggle Activo */}
                <label className="flex items-center gap-3 cursor-pointer">
                    <span className={`text-sm font-medium ${config.activo ? 'text-green-400' : 'text-gray-400'}`}>
                        {config.activo ? 'Reto Activo' : 'Desactivado'}
                    </span>
                    <div className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${config.activo ? 'bg-green-500' : 'bg-gray-600'}`}>
                        <input
                            type="checkbox"
                            className="sr-only"
                            checked={config.activo}
                            onChange={(e) => handleChange('activo', e.target.checked)}
                        />
                        <span className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${config.activo ? 'translate-x-6' : 'translate-x-1'}`} />
                    </div>
                </label>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                {/* Fecha Inicio */}
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-400">Fecha de Inicio (Opcional)</label>
                    <input
                        type="date"
                        value={config.inicio}
                        onChange={(e) => handleChange('inicio', e.target.value)}
                        className="w-full px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                    />
                    <p className="text-[10px] text-gray-500">Si se establece, el reto no aparecerá hasta esa fecha.</p>
                </div>

                {/* Reset Periodo */}
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-400">Periodo de Reset (Ranking)</label>
                    <select
                        value={config.reset_periodo}
                        onChange={(e) => handleChange('reset_periodo', e.target.value)}
                        className="w-full px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                    >
                        <option value="semanal">Semanal (Lun a Dom)</option>
                        <option value="quincenal">Quincenal (1-15, 16-fin)</option>
                        <option value="mensual">Mensual</option>
                    </select>
                </div>
                
                {/* Nº Preguntas */}
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-400">Número de Preguntas</label>
                    <input
                        type="number"
                        min="1"
                        max="20"
                        value={config.preguntas}
                        onChange={(e) => handleChange('preguntas', parseInt(e.target.value) || 8)}
                        className="w-full px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                    />
                </div>

                {/* Puntos Participación */}
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-400">Puntos Base (Participación)</label>
                    <input
                        type="number"
                        min="0"
                        value={config.pts_participacion}
                        onChange={(e) => handleChange('pts_participacion', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                    />
                </div>

                {/* Puntos Acierto */}
                <div className="space-y-1">
                    <label className="text-xs font-medium text-gray-400">Puntos por Acierto extra</label>
                    <input
                        type="number"
                        min="0"
                        value={config.pts_acierto}
                        onChange={(e) => handleChange('pts_acierto', parseInt(e.target.value) || 0)}
                        className="w-full px-3 py-2 bg-[var(--bg-input)] border border-[var(--border-color)] rounded-lg text-sm text-[var(--text-primary)] focus:outline-none focus:border-indigo-500"
                    />
                </div>
            </div>

            <div className="flex justify-end pt-4 mt-4 border-t border-[var(--glass-border)]">
                <button
                    onClick={handleSave}
                    disabled={saving}
                    className="flex items-center gap-2 px-4 py-2 bg-indigo-600 hover:bg-indigo-700 text-white rounded-lg text-sm transition-colors disabled:opacity-50"
                >
                    {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
                    Guardar Configuración
                </button>
            </div>
        </div>
    );
};

export default OpeAdminRetoConfig;
